import React, { Component } from 'react';
import { Helmet } from 'react-helmet';

// Layout
import Header from '../layout/header';
import Footer from '../layout/footer';

// Elements
import AvailableCitiesModal from '../elements/available-cities-modal';
import { startingPriceLabel } from '../../utils/pricing';

// Premium components
import {
  PremiumHero,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FinalCTA,
  Breadcrumbs,
  StructuredData,
} from '../elements/premium';

const STEPS = [
  { no: '01', icon: '📱', title: 'Choose Vehicle & Service', desc: 'Select your vehicle brand and model, then pick from a wide range of services — maintenance, repair, AC, battery, tyres, and more. See transparent pricing upfront.' },
  { no: '02', icon: '🚐', title: 'Mechanic Arrives at Your Door', desc: 'Our certified, uniformed mechanic arrives at your scheduled time — fully equipped with tools, parts, and diagnostic equipment. Home, office, or anywhere.' },
  { no: '03', icon: '🔧', title: 'Service & Real-Time Updates', desc: 'Track progress in real-time via our app. Get before/after photos, transparent updates on every repair, and approval before any additional work.' },
  { no: '04', icon: '✅', title: 'Quality Check & Warranty', desc: 'After a thorough quality check, you receive a digital job card and invoice. Every service is backed by a 30-day warranty on parts and labour.' },
];

const BENEFITS = [
  { icon: '⏱️', title: 'Save Time', desc: 'No waiting at garages. Service happens while you work, relax, or go about your day.' },
  { icon: '👁️', title: 'Full Transparency', desc: 'See your vehicle being serviced. Get photo updates. Know exactly what\'s being done and why.' },
  { icon: '💵', title: 'Fair Pricing', desc: 'Prices shown upfront before booking. No hidden charges. Pay only for what you approve.' },
  { icon: '🔧', title: 'Expert Mechanics', desc: 'Trained, background-verified professionals who handle your vehicle with care and precision.' },
  { icon: '🏷️', title: 'Genuine Parts', desc: 'OEM parts only. No compromises on quality for any repair or replacement.' },
  { icon: '🛡️', title: '30-Day Warranty', desc: 'Complete peace of mind. If any issue related to our service arises within 30 days, we fix it free.' },
];

class HowWeWork extends Component {
  render() {
    const { isMobileBrowser, isMobileApp, user, setUser, citiesModal, setCitiesModal } = this.props;

    const schema = {
      '@type': 'HowTo',
      name: 'How to Book Doorstep Vehicle Service with Ride N Repair',
      description: 'Book a certified mechanic to your doorstep in 4 simple steps.',
      step: STEPS.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.title,
        text: s.desc,
      })),
    };

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <link rel="canonical" href="https://www.ridenrepair.com/how-ride-n-repair-works" />
          <title>How It Works — Doorstep Vehicle Service | Ride N Repair</title>
          <meta name="description" content="Book a certified mechanic to your doorstep in 4 simple steps. Choose your service, mechanic arrives, real-time updates, 30-day warranty. See how Ride N Repair works." />
          <meta property="og:title" content="How Ride N Repair Works" />
          <meta property="og:url" content="https://www.ridenrepair.com/how-ride-n-repair-works" />
        </Helmet>

        <StructuredData data={schema} />

        {citiesModal && <AvailableCitiesModal closeModal={() => setCitiesModal(false)} />}

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge="How It Works"
          title="Doorstep Service in 4 Simple Steps"
          subtitle="From booking to warranty — here's how Ride N Repair makes vehicle service effortless"
          isMobileBrowser={isMobileBrowser}
          ctas={[
            { label: `Book Now — Starting ${startingPriceLabel('site')}`, href: '/service', variant: 'primary' },
          ]}
          trustItems={[
            { value: '60 sec', label: 'To Book' },
            { value: '15 Min', label: 'Arrival Time' },
            { value: '4.8★', label: 'Rating' },
            { value: '30 Days', label: 'Warranty' },
          ]}
        />

        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'How It Works' }]} />

        {/* STEPS */}
        <SectionWrapper variant="dark" kicker="The Process" heading="How Ride N Repair Works">
          <StepCards steps={STEPS} variant="dark" />
        </SectionWrapper>

        {/* BENEFITS */}
        <SectionWrapper variant="gray" kicker="Why Doorstep" heading="Why Doorstep Service Beats the Garage">
          <WhyUsGrid items={BENEFITS} />
        </SectionWrapper>

        {/* REFERRAL */}
        <SectionWrapper kicker="Bonus" heading="Refer Friends & Earn ₹500">
          <div className="rr-keyword__intro" style={{ maxWidth: 700, margin: '0 auto' }}>
            <p>
              Love the service? Share Ride N Repair with friends and family. They get <strong>₹500 off</strong> their
              first service, and you earn <strong>₹500</strong> once they complete it. Everyone wins.
            </p>
          </div>
        </SectionWrapper>

        {/* CTA */}
        <FinalCTA
          heading={<>Ready to Try It?<br />Book Your First Service Now.</>}
          subtitle={`Doorstep service · Certified mechanics · Starting ${startingPriceLabel('site')} · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default HowWeWork;
